/**
 * Schema Builder Utilities
 * Converts between FieldDefinition[] (UI state) and JSON Schema
 */

export type FieldType = 'string' | 'number' | 'integer' | 'boolean';
export type StringFormat = 'email' | 'uri' | 'date' | 'date-time' | undefined;

export interface FieldDefinition {
  id: string;
  name: string;
  type: FieldType;
  required: boolean;
  description?: string;
  // String-specific options
  enumValues?: string[];
  format?: StringFormat;
  minLength?: number;
  maxLength?: number;
  // Number-specific options
  minimum?: number;
  maximum?: number;
  // Default value
  defaultValue?: unknown;
}

export interface JsonSchemaProperty {
  type?: string;
  description?: string;
  enum?: string[];
  format?: string;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  default?: unknown;
}

export interface JsonSchema {
  type: 'object';
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
  // Index signature so JsonSchema satisfies Record<string, unknown> consumers
  [key: string]: unknown;
}

/**
 * Generate a unique ID for a field
 */
export const generateFieldId = (): string => {
  return `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create a new empty field definition
 */
export const createEmptyField = (): FieldDefinition => ({
  id: generateFieldId(),
  name: '',
  type: 'string',
  required: false,
});

/**
 * Convert FieldDefinition[] to JSON Schema
 */
export const fieldsToJsonSchema = (fields: FieldDefinition[]): JsonSchema | null => {
  if (fields.length === 0) {
    return null;
  }

  const properties: Record<string, JsonSchemaProperty> = {};
  const required: string[] = [];

  for (const field of fields) {
    if (!field.name.trim()) continue;

    const prop: JsonSchemaProperty = { type: field.type };

    if (field.description?.trim()) {
      prop.description = field.description.trim();
    }

    // String-specific properties
    if (field.type === 'string') {
      if (field.enumValues && field.enumValues.length > 0) {
        prop.enum = field.enumValues.filter(v => v.trim() !== '');
      }
      if (field.format) {
        prop.format = field.format;
      }
      if (field.minLength !== undefined && field.minLength > 0) {
        prop.minLength = field.minLength;
      }
      if (field.maxLength !== undefined && field.maxLength > 0) {
        prop.maxLength = field.maxLength;
      }
    }

    // Number-specific properties
    if (field.type === 'number' || field.type === 'integer') {
      if (field.minimum !== undefined) {
        prop.minimum = field.minimum;
      }
      if (field.maximum !== undefined) {
        prop.maximum = field.maximum;
      }
    }

    // Default value
    if (field.defaultValue !== undefined) {
      prop.default = field.defaultValue;
    }

    properties[field.name] = prop;

    if (field.required) {
      required.push(field.name);
    }
  }

  if (Object.keys(properties).length === 0) {
    return null;
  }

  const schema: JsonSchema = {
    type: 'object',
    properties,
  };

  if (required.length > 0) {
    schema.required = required;
  }

  return schema;
};

/**
 * Convert JSON Schema to FieldDefinition[]
 */
export const jsonSchemaToFields = (schema: Record<string, unknown> | null): FieldDefinition[] => {
  if (!schema) return [];

  const properties = schema.properties as Record<string, JsonSchemaProperty> | undefined;
  if (!properties) return [];

  const required = (schema.required as string[]) || [];
  const fields: FieldDefinition[] = [];

  for (const [name, prop] of Object.entries(properties)) {
    const field: FieldDefinition = {
      id: generateFieldId(),
      name,
      type: determineFieldType(prop),
      required: required.includes(name),
      description: prop.description,
    };

    // String-specific
    if (prop.enum) {
      field.enumValues = prop.enum;
    }
    if (prop.format) {
      field.format = prop.format as StringFormat;
    }
    if (prop.minLength !== undefined) {
      field.minLength = prop.minLength;
    }
    if (prop.maxLength !== undefined) {
      field.maxLength = prop.maxLength;
    }

    // Number-specific
    if (prop.minimum !== undefined) {
      field.minimum = prop.minimum;
    }
    if (prop.maximum !== undefined) {
      field.maximum = prop.maximum;
    }

    // Default
    if (prop.default !== undefined) {
      field.defaultValue = prop.default;
    }

    fields.push(field);
  }

  return fields;
};

/**
 * Determine field type from JSON Schema property
 */
const determineFieldType = (prop: JsonSchemaProperty): FieldType => {
  const type = prop.type;

  if (type === 'integer') return 'integer';
  if (type === 'number') return 'number';
  if (type === 'boolean') return 'boolean';

  // Default to string for unknown types
  return 'string';
};

/**
 * Get display label for a field type
 */
export const getFieldTypeLabel = (type: FieldType, hasEnum?: boolean, format?: StringFormat): string => {
  if (type === 'string' && hasEnum) return 'Dropdown';
  if (type === 'string' && format === 'email') return 'Email';
  if (type === 'string' && format === 'uri') return 'URL';
  if (type === 'string' && format === 'date') return 'Date';
  if (type === 'string' && format === 'date-time') return 'Date/Time';
  if (type === 'string') return 'Text';
  if (type === 'number') return 'Number';
  if (type === 'integer') return 'Integer';
  if (type === 'boolean') return 'Boolean';
  return type;
};

/**
 * Validate a field name (alphanumeric, underscore, no spaces at start)
 */
export const isValidFieldName = (name: string): boolean => {
  if (!name.trim()) return false;
  return /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name);
};

/**
 * Check if a field name is unique in the list
 */
export const isFieldNameUnique = (name: string, fields: FieldDefinition[], excludeId?: string): boolean => {
  return !fields.some(f => f.name === name && f.id !== excludeId);
};
